'use client';

import { c as _c } from "react/compiler-runtime";
import { PagesResizableSheet } from '@/domains/pages/shared/pages-resizable-sheet';
import { SavingsShareProgress } from '@/domains/pages/model-savings/components/savings-share-progress';
import { RecommendedMixLabel } from '@/domains/pages/model-savings/components/recommended-mix-label';
import { MODEL_SAVINGS_POSITIVE_COLOR, modelSavingsColorTint, SAVINGS_OPPORTUNITIES_EXPLANATION } from '@/domains/pages/model-savings/constants';
import { AIFI_DATA_NUMBER_CLASS } from '@/domains/pages/ai-proficiency/typography';
import type { SavingsScopeData, SavingsTask } from '@/domains/pages/model-savings/types';
import { MODEL_SAVINGS_TOP_FIVE_TASKS_LIMIT } from '@/domains/pages/model-savings/utils/model-savings-api';
import { formatAnnualSavings, formatCount } from '@/domains/pages/model-savings/utils/model-savings.utils';
import { formatScore } from '@/utils/format.util';
import { cn } from '@/utils/shad';
type SavingsOpportunitiesListDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: SavingsScopeData;
  tasks: SavingsTask[];
  tasksTotal: number;
  onTaskClick: (taskId: string) => void;
};
export function SavingsOpportunitiesListDrawer(t0) {
  const $ = _c(37);
  const {
    open,
    onOpenChange,
    data,
    tasks,
    tasksTotal,
    onTaskClick
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h2 className="text-lg leading-tight font-semibold text-foreground">Top savings opportunities</h2>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== data.topOpportunitiesSavingAnnual) {
    t2 = formatAnnualSavings(data.topOpportunitiesSavingAnnual);
    $[1] = data.topOpportunitiesSavingAnnual;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <span className={AIFI_DATA_NUMBER_CLASS}>{t2}</span>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = tasks.length;
  let t5;
  if ($[5] !== tasks.length || $[6] !== tasksTotal) {
    t5 = tasksTotal > tasks.length ? <>{" "}out of <span className={AIFI_DATA_NUMBER_CLASS}>{formatCount(tasksTotal)}</span> total</> : null;
    $[5] = tasks.length;
    $[6] = tasksTotal;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== data.topOpportunitiesShareOfSavingsPercent) {
    t6 = formatScore(data.topOpportunitiesShareOfSavingsPercent, {
      decimals: 0
    });
    $[8] = data.topOpportunitiesShareOfSavingsPercent;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = <span className={AIFI_DATA_NUMBER_CLASS}>{t6}%</span>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== data.savingAnnual) {
    t8 = formatAnnualSavings(data.savingAnnual);
    $[12] = data.savingAnnual;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <span className={AIFI_DATA_NUMBER_CLASS}>{t8}</span>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t3 || $[17] !== t5 || $[18] !== t7 || $[19] !== t9 || $[20] !== tasks.length) {
    t10 = <div className="shrink-0 border-b border-b-lightborder px-8 py-5">{t1}<p className="mt-0.5 text-sm text-muted-foreground">{t3} / year from the top {t4} opportunities{t5}, equal to{" "}{t7}{" "}of the {t9} total savings potential.</p></div>;
    $[16] = t3;
    $[17] = t5;
    $[18] = t7;
    $[19] = t9;
    $[20] = tasks.length;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="shrink-0 border-b border-b-lightborder px-8 py-3"><p className="text-sm leading-snug text-muted-foreground">{SAVINGS_OPPORTUNITIES_EXPLANATION}</p></div>;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== onTaskClick || $[24] !== tasks) {
    let t13;
    if ($[26] !== onTaskClick) {
      t13 = (task, index) => <OpportunityRow key={task.id} task={task} rank={index + 1} highlighted={index < MODEL_SAVINGS_TOP_FIVE_TASKS_LIMIT} onClick={() => onTaskClick(task.id)} />;
      $[26] = onTaskClick;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    t12 = tasks.map(t13);
    $[23] = onTaskClick;
    $[24] = tasks;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[28] !== t12) {
    t13 = <div className="min-h-0 flex-1 overflow-y-auto px-8 py-5"><div className="flex flex-col gap-2">{t12}</div></div>;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t10 || $[31] !== t13) {
    t14 = <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{t10}{t11}{t13}</div>;
    $[30] = t10;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== onOpenChange || $[34] !== open || $[35] !== t14) {
    t15 = <PagesResizableSheet open={open} onOpenChange={onOpenChange}>{t14}</PagesResizableSheet>;
    $[33] = onOpenChange;
    $[34] = open;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  return t15;
}
function OpportunityRow(t0) {
  const $ = _c(31);
  const {
    task,
    rank,
    highlighted: t1,
    onClick
  } = t0;
  const highlighted = t1 === undefined ? false : t1;
  const t2 = highlighted ? "" : "bg-muted/40 text-muted-foreground";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-medium", AIFI_DATA_NUMBER_CLASS, t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== highlighted) {
    t4 = highlighted ? {
      backgroundColor: modelSavingsColorTint(MODEL_SAVINGS_POSITIVE_COLOR, "22"),
      color: MODEL_SAVINGS_POSITIVE_COLOR
    } : undefined;
    $[2] = highlighted;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== rank || $[5] !== t3 || $[6] !== t4) {
    t5 = <span className={t3} style={t4}>{rank}</span>;
    $[4] = rank;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== task.name) {
    t6 = <p className="text-sm font-medium text-foreground">{task.name}</p>;
    $[8] = task.name;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== task.recommendedMix) {
    t7 = <RecommendedMixLabel mix={task.recommendedMix} className="mt-0.5" />;
    $[10] = task.recommendedMix;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <div className="min-w-0 flex-1">{t6}{t7}</div>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t10;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cn("text-sm font-semibold", AIFI_DATA_NUMBER_CLASS);
    t10 = {
      color: MODEL_SAVINGS_POSITIVE_COLOR
    };
    $[15] = t10;
    $[16] = t9;
  } else {
    t10 = $[15];
    t9 = $[16];
  }
  let t11;
  if ($[17] !== task.savingAnnual) {
    t11 = formatAnnualSavings(task.savingAnnual);
    $[17] = task.savingAnnual;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11) {
    t12 = <span className={t9} style={t10}>{t11}</span>;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== task.savingSharePercent) {
    t13 = <div className="flex items-center gap-1"><SavingsShareProgress value={task.savingSharePercent} size={18} /></div>;
    $[21] = task.savingSharePercent;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t12 || $[24] !== t13) {
    t14 = <div className="flex shrink-0 flex-col items-end gap-0.5">{t12}{t13}</div>;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== onClick || $[27] !== t14 || $[28] !== t5 || $[29] !== t8) {
    t15 = <button type="button" onClick={onClick} className="flex w-full items-start gap-3 rounded-lg border border-lightborder px-4 py-3 text-left transition-colors hover:bg-muted/20">{t5}{t8}{t14}</button>;
    $[26] = onClick;
    $[27] = t14;
    $[28] = t5;
    $[29] = t8;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
}